import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import SuperAdminPayrollFilters from "../../components/SuperAdminPayrollReports/SuperAdminPayrollFilters";
import SuperAdminPayrollTable from "../../components/SuperAdminPayrollReports/SuperAdminPayrollTable";
import {
  ScheduleJobList4,
  ListTransFileRecs,
  GetMerchantProfile,
} from "../../util/API/batch";
import { getPortalUsers } from "../../util/API/admin";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";

const PAYROLL_CATEGORY_ID = "33";

// ─── Module-level — available to all functions, no hoisting issues ───
function parseCustomDate(dateStr) {
  if (!dateStr || dateStr === "—") return new Date(0);
  const parts = dateStr.trim().split(" ");
  const datePart = parts[0];
  const timePart = parts[1] || "00:00:00";
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return new Date(0);
  return new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
}

function getStatusDisplay(code) {
  const map = {
    "-2": "Open",
    "-1": "Review",
    "0": "Approved for Processing",
    "1": "Approved for Processing",
    "5": "Rejected",
    "3": "Completed",
    "2": "Error",
    "4": "Cancelled",
  };
  return map[String(code)] || `Unknown (${code})`;
}

function SuperAdminPayrollReports() {
  const [allBatches, setAllBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");

  // ─── Fetch all batches once on mount ───
  const fetchAllBatches = async () => {
    setLoading(true);
    try {
      const batchResponse = await ScheduleJobList4({
        categoryid: PAYROLL_CATEGORY_ID,
        rows: 1000,
      });

      if (batchResponse.callStatus !== "1") {
        setAllBatches([]);
        setFilteredBatches([]);
        setLoading(false);
        return;
      }

      let raw = batchResponse.payload?.record || [];
      if (!Array.isArray(raw)) raw = raw ? [raw] : [];

      const usersResponse = await getPortalUsers();
      const userList = usersResponse?.payload || [];
      const usersArray = Array.isArray(userList) ? userList : userList ? [userList] : [];

      const companyMap = {};
      usersArray.forEach((user) => {
        const roles = Array.isArray(user.Securityuserrole)
          ? user.Securityuserrole
          : user.Securityuserrole ? [user.Securityuserrole] : [];

        if (
          roles.some((role) => role.roleid === "847") &&
          user.Vuser?.id &&
          user.Vuser?.otherNames
        ) {
          companyMap[user.Vuser.id] =
            (user.Vuser.otherNames || "")
              .replace(/&amp;amp;/g, "&")
              .replace(/&amp;#039;/g, "'")
              .replace(/&amp;#39;/g, "'")
              .replace(/&amp;/g, "&")
              .replace(/&quot;/g, '"')
              .replace(/&#34;/g, '"')
              .replace(/&#039;/g, "'")
              .replace(/&lt;/g, "<")
              .replace(/&gt;/g, ">")
              .replace(/&#39;/g, "'")
              .trim() || "Unknown Company";
        }
      });

      // ─── Collect unique user IDs ───
      const uniqueUserIds = [
        ...new Set(
          raw.map((item) => item.Scheduleinstance.requestuserid).filter(Boolean)
        ),
      ];

      // ─── Fetch profiles in parallel ───
      const profiles = await Promise.all(
        uniqueUserIds.map(async (uid) => {
          try {
            const res = await GetMerchantProfile("", uid);
            return { userid: uid, profile: res };
          } catch (err) {
            console.error(`Failed to fetch profile for userid ${uid}:`, err);
            return { userid: uid, profile: null };
          }
        })
      );

      profiles.forEach((p) => {
        if (p.profile?.callStatus === "1") {
          const businessname =
            p.profile.payload?.Merchant?.businessname || "Unknown Company";
          companyMap[p.userid] = businessname
            .replace(/&amp;amp;/g, "&")
            .replace(/&amp;#039;/g, "'")
            .replace(/&amp;#39;/g, "'")
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, '"')
            .replace(/&#34;/g, '"')
            .replace(/&#039;/g, "'")
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .trim();
        } else {
          companyMap[p.userid] = "Unknown Company";
        }
      });

      const mapped = raw
        .map((item) => {
          const s = item.Scheduleinstance;
          const recordsMatch = s.notes?.match(/records:\s*(\d+)/i);
          const totalRecords = recordsMatch ? parseInt(recordsMatch[1], 10) : 0;
          const scheduledDateStr = s.scheduletime || s.requesttime || "—";
          const companyName = companyMap[s.requestuserid] || "Unknown Company";

          return {
            id: s.requestid,
            fileName: s.inputfilename || "",
            companyName,
            records: totalRecords,
            scheduledDateStr,
            statusCode: s.executionstatus,
            status: getStatusDisplay(s.executionstatus),
            notes: s.notes || "",
            requestuserid: s.requestuserid,
            totalAmount: parseFloat(s.amount) || 0,
            totalAmountDisplay: parseFloat(s.amount)
              ? parseFloat(s.amount).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })
              : "—",
          };
        })
        // ─── Sort descending — latest first ───
        .sort(
          (a, b) =>
            parseCustomDate(b.scheduledDateStr) -
            parseCustomDate(a.scheduledDateStr)
        );

      // ─── Apply DAILY filter on initial load ───
      const today = format(new Date(), "yyyy-MM-dd");
      const dailyFiltered = mapped.filter(
        (b) => b.scheduledDateStr?.split(" ")[0] === today
      );

      setAllBatches(mapped);
      setFilteredBatches(dailyFiltered);
    } catch (err) {
      console.error("Failed to fetch payroll batches:", err);
      setAllBatches([]);
      setFilteredBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllBatches();
  }, []);

  // ─── Apply filters when Run Report is clicked ───
  const applyFilters = () => {
    let filtered = [...allBatches];
    const now = new Date();

    if (duration === "1") {
      // ─── Daily: string compare — no timezone risk ───
      const today = format(now, "yyyy-MM-dd");
      filtered = filtered.filter(
        (b) => b.scheduledDateStr && b.scheduledDateStr.split(" ")[0] === today
      );

    } else if (duration === "2") {
      // ─── Weekly: local time via parseCustomDate ───
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      weekStart.setHours(0, 0, 0, 0);
      weekEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= weekStart && d <= weekEnd;
      });

    } else if (duration === "3") {
      // ─── Monthly: local time via parseCustomDate ───
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      monthStart.setHours(0, 0, 0, 0);
      monthEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= monthStart && d <= monthEnd;
      });

    } else if (duration === "4" && startDate && endDate) {
      // ─── Custom range: manual construction — no UTC shift ───
      const [sy, sm, sd] = startDate.split("-").map(Number);
      const [ey, em, ed] = endDate.split("-").map(Number);
      const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);

      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= start && d <= end;
      });
    }

    // ─── Company filter ───
    if (selectedCompany !== "all") {
      filtered = filtered.filter((b) => b.companyName === selectedCompany);
    }

    setFilteredBatches(filtered);
  };

  const handleRunReport = () => {
    applyFilters();
  };

  // ─── Download CSV ───
  const getPayrollFileName = (inputFilename, statusCode) => {
    const baseName = (inputFilename || "batch").replace(/\.csv$/i, "");
    const code = String(statusCode || "");
    let suffix = "";
    if (code === "3") suffix = "_success";
    else if (code === "2") suffix = "_err";
    return `${baseName}_records${suffix}.csv`;
  };

  const handleDownloadCSV = async (batch) => {
    try {
      const res = await ListTransFileRecs(batch.id);

      if (res.callStatus !== "1") {
        alert("Could not load records for download.");
        return;
      }

      const rawArray = res.payload?.record || [];
      const recordsArray = Array.isArray(rawArray)
        ? rawArray
        : rawArray ? [rawArray] : [];
      const allRecords = recordsArray
        .map((item) => item.Transfilerecs)
        .filter(Boolean);

      if (allRecords.length === 0) {
        alert("No records to download.");
        return;
      }

      const headers = [
        "First Name", "Last Name", "Email", "Phone", "Amount", "Currency",
        "Address Line 1", "Address Line 2", "Country", "State", "Zip Code",
        "Date of Birth", "Comment", "Purpose of Account",
        "Account Add Success Message", "Fund Add Success Message",
      ];

      const rows = allRecords.map((r) => {
        const identifier = r.phoneno ? r.phoneno : r.email;
        const accountMessage =
          r.usercreated === "0"
            ? `${identifier}@4pay.ph user already exists`
            : r.usercreated === "1"
            ? `${identifier} user created successfully`
            : "";

        return [
          (r.firstname || "").replace(/"/g, '""'),
          (r.lastname || "").replace(/"/g, '""'),
          (r.email || "").replace(/"/g, '""'),
          (r.phoneno || "").replace(/"/g, '""'),
          r.amount || "0.00",
          r.currency || "",
          (r.address1 || "").replace(/"/g, '""'),
          (r.address2 || "").replace(/"/g, '""'),
          r.country || "",
          r.state || "",
          r.zipcode || "",
          r.birthdate || "",
          (r.comment || "").replace(/"/g, '""'),
          (r.purposeofaccount || "").replace(/"/g, '""'),
          accountMessage.replace(/"/g, '""'),
          (r.msg || "").replace(/"/g, '""'),
        ];
      });

      const csv = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getPayrollFileName(batch.fileName, batch.statusCode);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 40000);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Error generating CSV file.");
    }
  };

  // ─── Unique companies for dropdown ───
  const companies = [
    "all",
    ...new Set(allBatches.map((b) => b.companyName)),
  ].sort();

  // ─── Totals from visible batches ───
  const totalEmployees = filteredBatches.reduce((sum, b) => sum + b.records, 0);
  const grandTotalAmount = filteredBatches.reduce(
    (sum, b) => sum + (parseFloat(b.totalAmount) || 0),
    0
  );

  return (
    <Box className="page-container">
      <Box className="header-container" sx={{ mb: 4 }}>
        <Typography className="page-title">NCS Payroll Reports</Typography>
      </Box>

      <SuperAdminPayrollFilters
        duration={duration}
        setDuration={setDuration}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onRunReport={handleRunReport}
        companies={companies}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
      />

      {/* Summary Box */}
      <Box
        sx={{
          backgroundColor: "#5523D2",
          color: "white",
          p: 3,
          borderRadius: 2,
          mb: 4,
          textAlign: "center",
          fontSize: "1.3rem",
          fontWeight: "bold",
        }}
      >
        Total Employees: {totalEmployees}
        &nbsp;&nbsp;|&nbsp;&nbsp; Total Amount:{" "}
        <strong>
          ${grandTotalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </strong>
      </Box>

      <SuperAdminPayrollTable
        batches={filteredBatches}
        loading={loading && allBatches.length === 0}
        onDownload={handleDownloadCSV}
      />
    </Box>
  );
}

export default SuperAdminPayrollReports;
